/**
 * Échantillonnage de la géométrie RÉELLE d'une entité DR pour l'analyse du
 * plan — les points que la passe photos mesure (distance au tracé lui-même,
 * pas à sa bbox). Polylignes (arcs discrétisés via le bulge), lignes,
 * cercles, arcs, ellipses, contours de hachures. Décimé à ECHANTILLON_MAX
 * points : un main levé de 400 sommets n'a pas besoin de tous pour situer
 * une photo.
 *
 * Best-effort : `null` pour les natures non géométriques (texte, bloc) ou
 * illisibles — l'analyse retombe alors sur la bbox.
 */

import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { Point } from "@/utils/plan-analysis/types";
import { deleteObj } from "../core/delete";

export interface SampledGeometry {
  points: Point[];
  closed: boolean;
}

/** Nombre max de points conservés par entité (décimation régulière). */
const SAMPLE_MAX = 48;
/** Points par arc discrétisé (bulge, arc, cercle). */
const ARC_STEPS = 8;
const CIRCLE_STEPS = 16;

/** Décimation régulière en gardant premier et dernier points. */
function decimate(points: Point[]): Point[] {
  if (points.length <= SAMPLE_MAX) return points;
  const result: Point[] = [];
  const step = (points.length - 1) / (SAMPLE_MAX - 1);
  for (let i = 0; i < SAMPLE_MAX; i++) {
    result.push(points[Math.round(i * step)]);
  }
  return result;
}

/**
 * Discrétise un segment d'arc défini par bulge (b = tan(θ/4)) entre deux
 * sommets : points intermédiaires exclus des extrémités.
 */
function bulgePoints(a: Point, b: Point, bulge: number): Point[] {
  const chord = Math.hypot(b.x - a.x, b.y - a.y);
  if (bulge === 0 || chord === 0) return [];
  const theta = 4 * Math.atan(bulge);
  const radius = chord / (2 * Math.sin(Math.abs(theta) / 2));
  // Centre : milieu de la corde décalé perpendiculairement.
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const sagitta = radius * Math.cos(theta / 2) * Math.sign(bulge);
  const nx = -(b.y - a.y) / chord;
  const ny = (b.x - a.x) / chord;
  const center = { x: mid.x - nx * sagitta, y: mid.y - ny * sagitta };
  const startAngle = Math.atan2(a.y - center.y, a.x - center.x);
  const points: Point[] = [];
  for (let i = 1; i < ARC_STEPS; i++) {
    const angle = startAngle + (theta * i) / ARC_STEPS;
    points.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
    });
  }
  return points;
}

function samplePolyline(
  module: DrawingModuleType,
  polyline: DrawingModule.OdDbPolyline,
): SampledGeometry | null {
  const numVerts = polyline.numVerts();
  if (numVerts < 1) return null;
  const closed = polyline.isClosed();
  const verts: Array<Point & { bulge: number }> = [];
  const pt = new module.OdGePoint2d(0, 0);
  for (let i = 0; i < numVerts; i++) {
    polyline.getPointAt(i, pt); // rempli par référence
    verts.push({ x: pt.x, y: pt.y, bulge: polyline.getBulgeAt(i) });
  }
  deleteObj(pt);
  const points: Point[] = [];
  const segCount = closed ? numVerts : numVerts - 1;
  for (let i = 0; i < numVerts; i++) {
    const a = verts[i];
    points.push({ x: a.x, y: a.y });
    if (i < segCount) {
      const b = verts[(i + 1) % numVerts];
      points.push(...bulgePoints(a, b, a.bulge));
    }
  }
  return { points: decimate(points), closed };
}

function sampleArc(
  center: Point,
  radius: number,
  start: number,
  sweep: number,
  steps: number,
): Point[] {
  const points: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const angle = start + (sweep * i) / steps;
    points.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
    });
  }
  return points;
}

function sampleHatch(
  module: DrawingModuleType,
  hatch: DrawingModule.OdDbHatch,
): SampledGeometry | null {
  // Un seul contour suffit : le plus grand loop (contour extérieur) porte
  // l'emprise, les îlots n'aident pas à situer une photo.
  const numLoops = hatch.numLoops();
  let best: Point[] = [];
  for (let loop = 0; loop < numLoops; loop++) {
    const vertices = new module.OdGePoint2dArray();
    const bulges = new module.OdGeDoubleArray();
    try {
      hatch.getLoopAt1(loop, vertices, bulges);
      const count = vertices.size();
      const points: Point[] = [];
      for (let i = 0; i < count; i++) {
        const v = vertices.get(i);
        const a = { x: v.x, y: v.y };
        points.push(a);
        const bulge = i < bulges.size() ? bulges.get(i) : 0;
        if (bulge !== 0) {
          const w = vertices.get((i + 1) % count);
          points.push(...bulgePoints(a, { x: w.x, y: w.y }, bulge));
          deleteObj(w);
        }
        deleteObj(v);
      }
      if (points.length > best.length) best = points;
    } catch {
      // loop non polygonal (arêtes courbes) — ignoré, la bbox fera foi
    } finally {
      deleteObj(vertices);
      deleteObj(bulges);
    }
  }
  return best.length > 0 ? { points: decimate(best), closed: true } : null;
}

/** Échantillonne une entité déjà ouverte en lecture. */
export function sampleEntityGeometry(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
): SampledGeometry | null {
  try {
    const polyline = module.OdDbPolyline.cast(entity);
    if (polyline) {
      const sampled = samplePolyline(module, polyline);
      deleteObj(polyline);
      return sampled;
    }

    const line = module.OdDbLine.cast(entity);
    if (line) {
      const start = line.startPoint();
      const end = line.endPoint();
      const sampled = {
        points: [
          { x: start.x, y: start.y },
          { x: end.x, y: end.y },
        ],
        closed: false,
      };
      deleteObj(start);
      deleteObj(end);
      deleteObj(line);
      return sampled;
    }

    const circle = module.OdDbCircle.cast(entity);
    if (circle) {
      const c = circle.center();
      const points = sampleArc(
        { x: c.x, y: c.y },
        circle.radius(),
        0,
        2 * Math.PI,
        CIRCLE_STEPS,
      );
      deleteObj(c);
      deleteObj(circle);
      // Le dernier point referme sur le premier : inutile en contour fermé.
      return { points: points.slice(0, -1), closed: true };
    }

    const arc = module.OdDbArc.cast(entity);
    if (arc) {
      const c = arc.center();
      let sweep = arc.endAngle() - arc.startAngle();
      if (sweep <= 0) sweep += 2 * Math.PI;
      const points = sampleArc(
        { x: c.x, y: c.y },
        arc.radius(),
        arc.startAngle(),
        sweep,
        ARC_STEPS,
      );
      deleteObj(c);
      deleteObj(arc);
      return { points, closed: false };
    }

    const ellipse = module.OdDbEllipse.cast(entity);
    if (ellipse) {
      const c = ellipse.center();
      const major = ellipse.majorAxis();
      const a = ellipse.majorRadius();
      const b = ellipse.minorRadius();
      const rotation = Math.atan2(major.y, major.x);
      const points: Point[] = [];
      for (let i = 0; i < CIRCLE_STEPS; i++) {
        const t = (2 * Math.PI * i) / CIRCLE_STEPS;
        const x = a * Math.cos(t);
        const y = b * Math.sin(t);
        points.push({
          x: c.x + x * Math.cos(rotation) - y * Math.sin(rotation),
          y: c.y + x * Math.sin(rotation) + y * Math.cos(rotation),
        });
      }
      deleteObj(c);
      deleteObj(major);
      deleteObj(ellipse);
      return { points, closed: true };
    }

    const hatch = module.OdDbHatch.cast(entity);
    if (hatch) {
      const sampled = sampleHatch(module, hatch);
      deleteObj(hatch);
      return sampled;
    }
  } catch {
    // entité illisible — la bbox fera foi
  }
  return null;
}
